import React, { useId } from "react";
import { Box, Switch, Typography } from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { IncognitoGlyph } from "./IncognitoAvatar";
import { theme } from "../../theme/colors";

function ComposerLabeledSwitch({ checked, onChange, disabled }) {
  return (
    <Box
      component="button"
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      sx={{
        position: "relative",
        width: 50,
        height: 28,
        borderRadius: "14px",
        border: "none",
        p: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        bgcolor: checked ? theme.primary : "#e2e8f0",
        opacity: disabled ? 0.55 : 1,
        flexShrink: 0,
      }}
    >
      {checked ? (
        <Typography
          component="span"
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "12px",
            fontWeight: 600,
            color: "#ffffff",
            lineHeight: 1,
            pr: "1.5px",
          }}
        >
          On
        </Typography>
      ) : null}
      <Box
        sx={{
          position: "absolute",
          top: 3,
          left: checked ? "auto" : 3,
          right: checked ? 1.5 : "auto",
          width: 22,
          height: 22,
          borderRadius: "50%",
          bgcolor: "#ffffff",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
        }}
      />
    </Box>
  );
}

/**
 * "Post anonymously" switch shared by the post composer and comment boxes.
 * `compact` shrinks it for comment and reply inputs.
 */
export default function AnonymousToggle({
  checked,
  onChange,
  disabled = false,
  helperText,
  compact = false,
  label = "Post anonymously",
}) {
  const helperId = useId();

  if (!compact) {
    return (
      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          bgcolor: "#ffffff",
          px: 1.5,
          py: 1,
          mt: -0.75,
          mb: 1.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            <VisibilityOffOutlinedIcon
              sx={{ fontSize: 18, color: disabled ? "#64748b" : "#0f172a" }}
              aria-hidden
            />
            <Typography
              component="span"
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: disabled ? "#475569" : "#0f172a",
                lineHeight: 1.25,
              }}
            >
              {label}
            </Typography>
          </Box>
          <ComposerLabeledSwitch checked={checked} onChange={onChange} disabled={disabled} />
        </Box>
        {helperText ? (
          <Typography
            id={helperId}
            sx={{
              mt: 0.5,
              fontSize: "0.8125rem",
              lineHeight: 1.35,
              color: "#475569",
            }}
          >
            {helperText}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minHeight: 44 }}>
      <IncognitoGlyph size={16} color={disabled ? "#475569" : "#0f172a"} />
      <Typography
        component="span"
        sx={{
          flex: 1,
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: disabled ? "#475569" : "#334155",
        }}
      >
        {label}
      </Typography>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        color="primary"
        disabled={disabled}
      />
    </Box>
  );
}
